import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CustomField } from "@web-app/types";

import {
  CreateConnectionPayload,
  create,
  get,
  getCustomFields,
} from "../services/workspaceConnections";

import { useSessionUser } from "./useAuth";

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

export const useWorkspaceConnections = () => {
  const { data: sessionUser } = useSessionUser();
  return useQuery({
    enabled: !!sessionUser?.currentWorkspaceId,
    queryFn: async () => {
      const response = await get(sessionUser?.currentWorkspaceId || "");
      return response;
    },
    queryKey: ["workspaceConnections", sessionUser?.currentWorkspaceId || ""],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
};

export const useWorkspaceConnectionMutation = () => {
  const { data: sessionUser } = useSessionUser();
  const queryClient = useQueryClient();
  const currentWorkspaceId = sessionUser?.currentWorkspaceId || "";

  const createConnection = useMutation({
    mutationFn: async (payload: CreateConnectionPayload) => {
      return create(currentWorkspaceId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["workspaceConnections", currentWorkspaceId],
      });
    },
  });

  return { createConnection };
};

export const useWorkspaceActiveCustomFields = () => {
  const { data: sessionUser } = useSessionUser();
  const {
    data: connections,
    isLoading: isLoadingConnections,
    isError: isErrorInConnections,
  } = useQuery({
    enabled: !!sessionUser?.currentWorkspaceId,
    queryFn: async () => {
      const response = await get(sessionUser?.currentWorkspaceId || "");
      return response;
    },
    queryKey: [
      "workspaceConnections-customFields",
      sessionUser?.currentWorkspaceId || "",
    ],
    refetchOnWindowFocus: false,
  });

  const {
    data: customFields,
    isLoading: isLoadingCustomFields,
    isError: isErrorInCustomFields,
  } = useQuery({
    enabled: !!connections && !isLoadingConnections,
    queryFn: async () => {
      const response = await getCustomFields(
        connections?.map((connection) => connection.connectedWorkspaceId) || [],
      );
      return response;
    },
    queryKey: [
      "workspaceConnections-activeCustomFields",
      sessionUser?.currentWorkspaceId || "",
      connections?.map((connection) => connection.connectedWorkspaceId) || [],
    ],
    refetchOnWindowFocus: false,
  });

  return {
    customFields: customFields ?? EMPTY_CUSTOM_FIELDS,
    isError: isErrorInConnections || isErrorInCustomFields,
    isLoading: isLoadingConnections || isLoadingCustomFields,
  };
};
